// @owners { team: patients-team }
import {
  type FetchOrderPricingResponse,
  type LineItem,
  type LineItemType,
  LineItemTypeMap,
} from '@alto/scriptdash/alto/pricing/patients/v3/pricing_endpoint';
import { roundToTwo } from '~shared/helpers/currency';

/**
 * Line items subtracted from order `total` to get the amount charged on the primary (medication) card when split pay is on.
 * Delivery, tip, and similar fees stay on the secondary payment. Program dispensing fee stays on the primary with medication copay.
 */
const IGNORED_LINE_ITEMS_FOR_SPLIT_PAYMENT_SUBTOTAL = new Set<LineItemType>([
  'tip_amount',
  'on_demand_fee',
  'delivery_fee',
  'same_day_delivery_fee',
  'next_day_delivery_fee',
  'standard_delivery_fee',
  'asap_delivery_fee',
  'next_day_mail_fee',
]);

const getTotalLineItem = (data: FetchOrderPricingResponse) => data.line_items?.find(({ key }) => key === 'total');

/**
 * Med + dispensing + other non-ignored line items: order total minus delivery/tip (and related) fees.
 * Used as the split-payment cap for the first card (medication & dispensing).
 */
export const computeOrderSubtotalForSplitPayment = (data: FetchOrderPricingResponse): number | null => {
  const orderTotal = getTotalLineItem(data);
  if (!orderTotal) {
    return null;
  }

  const { line_items } = data;
  if (orderTotal.value === 0) {
    return 0;
  }

  if (!orderTotal.value) {
    return null;
  }

  let total = orderTotal.value;
  for (const lineItem of line_items) {
    const { key, value } = lineItem;

    if (IGNORED_LINE_ITEMS_FOR_SPLIT_PAYMENT_SUBTOTAL.has(key)) {
      total -= value ?? 0;
    }
  }
  return roundToTwo(total);
};

/** True when patient pricing includes a positive program dispensing fee (order-level line item). */
export const hasProgramDispensingFee = (lineItems: LineItem[] | undefined | null): boolean => {
  if (!lineItems?.length) {
    return false;
  }
  return lineItems.some(
    (item) =>
      item.key === LineItemTypeMap.TOTAL_PROGRAM_DISPENSING_FEE && typeof item.value === 'number' && item.value > 0,
  );
};
